// StraddleBuilder — Multi-leg strategy builder (Angel One Light Theme)
import React from 'react';
import { OPTION_STRATEGY_TEMPLATES } from '../../../config/tradingConstants';
import { FaTimes, FaPlay } from 'react-icons/fa';

export default function StraddleBuilder({ underlying, expiry, strikes, selectedLegs, setSelectedLegs, onTrade }) {
  const addLeg = (type, optionType, strikeOffset = 0) => {
    const midIdx = Math.floor(strikes.length / 2);
    const idx = Math.max(0, Math.min(strikes.length - 1, midIdx + strikeOffset));
    const strike = strikes[idx];
    if (!strike) return;

    setSelectedLegs(prev => [...prev, {
      strike: strike.strike,
      type,
      optionType,
      data: optionType === 'CE' ? strike.call : strike.put,
      qty: 1,
    }]);
  };

  const removeLeg = (index) => {
    setSelectedLegs(prev => prev.filter((_, i) => i !== index));
  };

  const applyTemplate = (template) => {
    const midIdx = Math.floor(strikes.length / 2);
    const legs = template.legs.map(leg => {
      const idx = Math.max(0, Math.min(strikes.length - 1, midIdx + (leg.offset || 0)));
      const strike = strikes[idx];
      return {
        strike: strike?.strike,
        type: leg.type,
        optionType: leg.option,
        data: leg.option === 'CE' ? strike?.call : strike?.put,
        qty: 1,
      };
    }).filter(l => l.strike);

    setSelectedLegs(legs);
  };

  const totalCost = selectedLegs.reduce((sum, leg) => {
    const price = leg.data?.lastPrice || 0;
    return sum + (leg.type === 'BUY' ? -price : price) * leg.qty;
  }, 0);

  return (
    <div className="space-y-4">
      {/* Strategy Templates */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-2">Quick Strategies</h4>
        <div className="flex flex-wrap gap-1.5">
          {OPTION_STRATEGY_TEMPLATES.map(t => (
            <button
              key={t.name}
              onClick={() => applyTemplate(t)}
              className="px-2.5 py-1 rounded-lg text-[10px] font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 transition-all"
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* Legs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Strategy Legs</h4>
          <div className="flex gap-1">
            <button onClick={() => addLeg('BUY', 'CE')} className="px-2 py-1 rounded text-[9px] font-bold bg-green-50 text-green-600 hover:bg-green-100 border border-green-200 transition-colors">+ Buy CE</button>
            <button onClick={() => addLeg('BUY', 'PE')} className="px-2 py-1 rounded text-[9px] font-bold bg-green-50 text-green-600 hover:bg-green-100 border border-green-200 transition-colors">+ Buy PE</button>
            <button onClick={() => addLeg('SELL', 'CE')} className="px-2 py-1 rounded text-[9px] font-bold bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-colors">+ Sell CE</button>
            <button onClick={() => addLeg('SELL', 'PE')} className="px-2 py-1 rounded text-[9px] font-bold bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-colors">+ Sell PE</button>
          </div>
        </div>

        {selectedLegs.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">Select a template or add legs manually</p>
        ) : (
          <div className="space-y-2">
            {selectedLegs.map((leg, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    leg.type === 'BUY'
                      ? 'text-green-600 bg-green-50 border border-green-200'
                      : 'text-red-500 bg-red-50 border border-red-200'
                  }`}>
                    {leg.type}
                  </span>
                  <span className="text-xs font-semibold text-gray-900">{underlying.label} {leg.strike} {leg.optionType}</span>
                  <span className="text-[10px] text-gray-400">{expiry}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-900">{leg.data?.lastPrice?.toFixed(2) || '—'}</span>
                  <button onClick={() => removeLeg(i)} className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                    <FaTimes className="text-[10px]" />
                  </button>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-[10px] text-gray-600">
                Net {totalCost <= 0 ? 'Debit' : 'Credit'}: <span className="font-mono font-semibold text-gray-900">{Math.abs(totalCost).toFixed(2)}</span>
              </span>
              <button
                className="px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm flex items-center gap-1.5 transition-colors"
                onClick={() => {
                  selectedLegs.forEach(leg => {
                    onTrade && onTrade({
                      ...leg.data,
                      tradingsymbol: `${underlying.label}${expiry}${leg.strike}${leg.optionType}`,
                      exchange: underlying.exchange,
                    }, leg.type);
                  });
                }}
              >
                <FaPlay className="text-[8px]" /> Execute
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
